import { useEffect, useMemo } from "react";

import { checkMdmClientHealth } from "@/app/lib/actions/cat/mdm-sync";
import {
  shouldShowCompanyRecap,
  shouldShowIndividualRecap,
} from "@/lib/cat/mdm-recap-from-draft";
import { useCurrentDraftStore } from "@/stores/selected-vehicle-store";
import { useMdmClientHealthStore } from "@/stores/mdm-client-health-store";

function buildMdmHealthCheckKey(
  draft: NonNullable<
    ReturnType<typeof useCurrentDraftStore.getState>["currentDraft"]
  >,
): string {
  return [
    draft.id,
    draft.clientId ?? "",
    draft.companyMdmSourceId ?? "",
    draft.rentSale ?? "",
    draft.directorSale ?? "",
    draft.exclusiveKind ?? "",
  ].join("|");
}

/**
 * Runs RefPers client health check while the retail flow is open.
 * Persists the message in a store so alerts stay visible on every step.
 */
export function useMdmClientHealthCheck() {
  const currentDraft = useCurrentDraftStore((state) => state.currentDraft);
  const setCurrentDraft = useCurrentDraftStore(
    (state) => state.setCurrentDraft,
  );
  const setHealthError = useMdmClientHealthStore(
    (state) => state.setHealthError,
  );
  const healthCheckKey = useMemo(
    () => (currentDraft ? buildMdmHealthCheckKey(currentDraft) : null),
    [currentDraft],
  );

  useEffect(() => {
    if (!currentDraft?.id || !healthCheckKey) {
      setHealthError(null);
      return;
    }

    const needCompany = shouldShowCompanyRecap(currentDraft);
    const needPerson = shouldShowIndividualRecap(currentDraft);
    const hasCompanyId = Boolean(currentDraft.companyMdmSourceId?.trim());
    const hasClientId = Boolean(currentDraft.clientId?.trim());

    if ((!needCompany || !hasCompanyId) && (!needPerson || !hasClientId)) {
      setHealthError(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      const result = await checkMdmClientHealth({ draftId: currentDraft.id });
      if (cancelled) return;

      if (result.status === "matched") {
        setHealthError(null);
        const updates: Partial<typeof currentDraft> = {};
        if (
          result.autoRematched &&
          result.clientId &&
          result.clientId !== currentDraft.clientId
        ) {
          updates.clientId = result.clientId;
        }
        if (Object.keys(updates).length > 0) {
          setCurrentDraft({ ...currentDraft, ...updates });
        }
        return;
      }

      if (result.message && result.errorKind) {
        setHealthError({
          message: result.message,
          errorKind: result.errorKind,
        });
      } else {
        setHealthError(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [healthCheckKey, setCurrentDraft, setHealthError]);
}
